import { useCallback, useEffect, useMemo, useRef } from 'react';

import _ from '@luna/lodash';
import { SSE_EVENTS_NAMESPACE } from '@luna/api';
import { useSseContext } from '@shared/providers';

interface ThrottleSettings {
    leading?: boolean;
    trailing?: boolean;
}

export function useSseEvent<T>(options: {
    eventType: string;
    throttleWait: number;
    throttleSettings?: ThrottleSettings;
    onEvent: (event: T[]) => void;
}): void;
export function useSseEvent<T>(options: { eventType: string; onEvent: (event: T) => void }): void;

/**
 *  Do not export this helper function as a public api,
 *  we don't want consumers to handle unsubscription by themselves
 */
export function useSseEvent<T>({
    eventType,
    onEvent,
    throttleWait,
    throttleSettings,
}: {
    eventType: string;
    throttleWait?: number;
    throttleSettings?: ThrottleSettings;
    onEvent: (event: T | T[]) => void;
}): void {
    const { value: { eventBus } = {} } = useSseContext();
    const payloadsBuffer = useRef<T[]>([]);

    const throttle = useMemo(
        // eslint-disable-next-line you-dont-need-lodash-underscore/throttle
        () => _.throttle((callback: () => void) => callback(), throttleWait, throttleSettings),
        [throttleWait, throttleSettings],
    );

    const handleEvent = useCallback(
        (payload: T) => {
            if (!throttleWait) {
                onEvent(payload);
                return;
            }

            payloadsBuffer.current = [...payloadsBuffer.current, payload];

            throttle(() => {
                onEvent(payloadsBuffer.current);
                payloadsBuffer.current = [];
            });
        },
        [throttleWait, throttle, onEvent],
    );

    useEffect(() => {
        const listener: EventListener = (event) => {
            handleEvent((event as CustomEvent).detail);
        };

        const type = `${SSE_EVENTS_NAMESPACE}:${eventType}`;
        eventBus?.addEventListener(type, listener);

        return () => {
            eventBus?.removeEventListener(type, listener);
        };
    }, [eventBus, eventType, handleEvent]);
}
